import {type ReactNode, useCallback, useState} from 'react'
import {Banner, Dialog, Link, Textarea} from '@primer/react'
import {ModelPicker} from './ModelPicker'
import {FixWithCopilotError, type CopilotFixDialogSubmitOptions, type KnownBillingEntityError} from './types'
import {SplitButton, type SplitButtonItem} from './SplitButton'
import styles from './CopilotFixDialog.module.css'

type ApplyChangesMode = 'commit' | 'pull_request'

export interface CopilotFixDialogProps {
  title: string
  onClose: () => void
  onSubmit: (options: CopilotFixDialogSubmitOptions) => Promise<void | boolean>
  modelsUrl?: string
  submitDisabled?: boolean
  /** When false, the dialog will not auto-close after a successful onSubmit. Defaults to true. */
  closeOnSubmit?: boolean
  children?: ReactNode
}

const APPLY_MODE_ITEMS: Array<SplitButtonItem<ApplyChangesMode>> = [
  {
    value: 'commit',
    label: 'Fix and commit',
    description: "Copilot will push a commit directly to this pull request's branch.",
  },
  {
    value: 'pull_request',
    label: 'Fix and open pull request',
    description: 'Copilot will create a new pull request with the changes.',
  },
]

export const CopilotFixDialog: React.FC<CopilotFixDialogProps> = ({
  title,
  onClose,
  onSubmit,
  modelsUrl,
  submitDisabled = false,
  closeOnSubmit = true,
  children,
}) => {
  const [applyMode, setApplyMode] = useState<ApplyChangesMode>('commit')
  const [isFixing, setIsFixing] = useState(false)
  const [errorInfo, setErrorInfo] = useState<{message: string; knownError?: KnownBillingEntityError} | null>(null)
  const [additionalInstructions, setAdditionalInstructions] = useState('')
  const [selectedModel, setSelectedModel] = useState('auto')

  const handleSubmit = useCallback(async () => {
    setIsFixing(true)
    setErrorInfo(null)
    try {
      const result = await onSubmit({
        createPullRequest: applyMode === 'pull_request',
        instructions: additionalInstructions.trim(),
        model: selectedModel,
      })
      if (closeOnSubmit && result !== false) {
        onClose()
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred while trying to Fix with Copilot. Please try again.'
      const knownError = error instanceof FixWithCopilotError ? error.knownError : undefined
      setErrorInfo({message, knownError})
    } finally {
      setIsFixing(false)
    }
  }, [onSubmit, applyMode, additionalInstructions, selectedModel, closeOnSubmit, onClose])

  return (
    <Dialog onClose={onClose} title={title} width="xlarge">
      <Dialog.Body>
        {errorInfo && (
          <Banner
            variant="critical"
            className={styles.errorBanner}
            role="alert"
            title={errorInfo.message}
            description={
              errorInfo.knownError ? (
                <p>
                  {'To update your billing entity, '}
                  <Link inline href={errorInfo.knownError.settingsPath}>
                    go to settings
                  </Link>
                  {'. '}
                  {'For more information, see the '}
                  <Link inline href={errorInfo.knownError.docsLink}>
                    documentation
                  </Link>
                  {'.'}
                </p>
              ) : undefined
            }
          />
        )}
        <div>
          <p className="fgColor-muted">
            Fix the selected review comments with Copilot cloud agent. You will be added as co-author on any new
            commits.
          </p>
        </div>
        <div className={styles.textareaContainer}>
          <Textarea
            className={styles.instructionsTextarea}
            aria-label="Additional instructions"
            placeholder="Add additional instructions..."
            value={additionalInstructions}
            onChange={e => setAdditionalInstructions(e.target.value)}
            rows={4}
            resize="none"
          />
          <div className={styles.textareaToolbar}>
            {modelsUrl && <ModelPicker modelsUrl={modelsUrl} onModelChange={setSelectedModel} />}
            <SplitButton
              items={APPLY_MODE_ITEMS}
              selectedValue={applyMode}
              onSelectionChange={setApplyMode}
              onAction={handleSubmit}
              variant="primary"
              size="small"
              disabled={isFixing || submitDisabled}
              loading={isFixing}
              dropdownAriaLabel="Change contribution method"
            />
          </div>
        </div>
        {children}
      </Dialog.Body>
    </Dialog>
  )
}
